import React, { useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useUpdateSupplier } from "../../hooks/api/useSupplier";
import { AddressInput, Supplier } from "./types/supplier";
import {
  Address,
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddressForm from "../common/AddressForm/AddressForm";

interface EditSupplierModalProps {
  open: boolean;
  onClose: () => void;
  supplier: Supplier;
}

interface SupplierFormData {
  name: string;
  email?: string;
  phone?: string;
  address?: Address;
}

const EditSupplierModal: React.FC<EditSupplierModalProps> = ({
  open,
  onClose,
  supplier
}) => {
  const updateSupplier = useUpdateSupplier();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const supplierSchema = Yup.object().shape({
    name: Yup.string().required(
      t("supplier-name-required", "Name is required")
    ),
    email: Yup.string()
      .email(t("supplier-email-invalid", "Invalid email format"))
      .test(
        "supplier-phone-or-email",
        t(
          "supplier-phone-or-email-required",
          "Provide a phone number or an email address"
        ),
        function (value) {
          const email = (value ?? "").trim();
          const phone = (
            (this.parent as { phone?: string | null }).phone ?? ""
          ).trim();
          return Boolean(phone || email);
        }
      )
      .optional()
      .nullable(),
    phone: Yup.string().optional().nullable(),
    address: Yup.object()
      .shape({
        country: Yup.string().optional().nullable(),
        province: Yup.string().optional().nullable(),
        district: Yup.string().optional().nullable(),
        sector: Yup.string().optional().nullable(),
        cell: Yup.string().optional().nullable(),
        village: Yup.string().optional().nullable(),
        streetAddress: Yup.string().optional().nullable()
      })
      .optional()
      .nullable()
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<SupplierFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(supplierSchema as any),
    mode: "onChange",
    defaultValues: {
      name: supplier.name,
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || {
        country: "Rwanda",
        province: "",
        district: "",
        sector: "",
        cell: "",
        village: "",
        streetAddress: ""
      }
    }
  });

  useEffect(() => {
    if (open && supplier) {
      reset({
        name: supplier.name,
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || {
          country: "Rwanda",
          province: "",
          district: "",
          sector: "",
          cell: "",
          village: "",
          streetAddress: ""
        }
      });
    }
  }, [open, supplier, reset]);

  const onSubmit: SubmitHandler<SupplierFormData> = async (data) => {
    try {
      const addr = data.address;
      const hasAddress =
        addr &&
        [
          addr.country,
          addr.province,
          addr.district,
          addr.sector,
          addr.cell,
          addr.village,
          addr.streetAddress
        ].some((x) => String(x ?? "").trim() !== "");

      await updateSupplier.mutateAsync({
        supplierId: supplier.id,
        updates: {
          name: data.name,
          email: data.email?.trim() ? data.email.trim() : null,
          phone: data.phone?.trim() ? data.phone.trim() : null,
          ...(hasAddress
            ? {
                address: {
                  country: addr!.country,
                  province: addr!.province,
                  district: addr!.district,
                  sector: addr!.sector,
                  cell: addr!.cell,
                  village: addr!.village,
                  streetAddress: addr!.streetAddress
                } as AddressInput
              }
            : {})
        }
      });
      onClose();
    } catch (error) {
      console.error("Failed to update supplier:", error);
    }
  };

  const isLoading = isSubmitting || updateSupplier.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("supplier-edit-title", "Edit Supplier")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden"
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: matchMD ? 2 : 3,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px"
              }
            }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("supplier-field-name", "Name")}
                  placeholder={t(
                    "supplier-field-name-placeholder",
                    "Enter supplier name"
                  )}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                />
              )}
            />

            {/* Simplified Phone Field - Optional */}
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("phone-number", "Phone Number")}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  placeholder="+2507..."
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("email", "Email")}
                  placeholder={t(
                    "supplier-field-email-placeholder",
                    "Enter email"
                  )}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Paper
              variant="outlined"
              sx={{
                p: matchMD ? 1.5 : 2,
                display: "flex",
                flexDirection: "column",
                gap: matchMD ? 1.5 : 2
              }}
            >
              <Typography
                variant={matchMD ? "body2" : "subtitle1"}
                fontWeight={600}
                color="textPrimary"
              >
                {t("address", "Address")}
              </Typography>
              <AddressForm
                control={control}
                defaultAddress={useWatch({ control, name: "address" })}
                setValue={setValue}
              />
            </Paper>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: matchMD ? 1 : 2,
              p: matchMD ? 2 : 3,
              pt: matchMD ? 1.5 : 2,
              borderTop: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
              backgroundColor: "background.paper"
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("supplier-save-changes", "Save Changes")}
            </LoadingButton>

            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditSupplierModal;
